import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, tap } from "rxjs";
import { BaseJobPollingService, PollingProcess } from "./base";
import { ProcessEvent } from "./polling";

type InputData = {
  file?: File;
  prompt?: string;
  documentType: "template_document" | "regular";
};

type RetryData = null;
type StartData = {
  documentId: string;
};

type CompleteData = null;

export type DocumentGenerationEvent = ProcessEvent<StartData, CompleteData>;

@Injectable({
  providedIn: "root",
})
export class DocumentGenerationService extends BaseJobPollingService<
  InputData,
  RetryData,
  StartData,
  CompleteData
> {
  private static POLLING_TIMEOUT = 600 * 1000; // 10 min
  private static POLLING_DELAY = 4 * 1000; // 4 sec
  private idToKey: Map<string, string>;

  constructor(protected http: HttpClient) {
    super(
      DocumentGenerationService.POLLING_TIMEOUT,
      DocumentGenerationService.POLLING_DELAY,
    );
    this.idToKey = new Map();
  }

  protected getKey(documentId: string): string {
    return this.idToKey.get(documentId) || "";
  }

  generate(
    documentType: "template_document" | "regular",
    file?: File,
    prompt?: string,
  ): Observable<ProcessEvent<StartData, CompleteData>> {
    const job = this.dispatch({ file, prompt, documentType });
    return job.process.pipe(
      tap((event) => {
        if (event.status === "start") {
          const templateId = event.data.data.documentId;
          this.idToKey.set(templateId, job.jobId);
        }
      }),
    );
  }

  regenerate(
    documentId: string,
  ): Observable<ProcessEvent<StartData, CompleteData>> {
    return this.retry(this.getKey(documentId), null).process;
  }

  currentProcess(
    documentId: string,
  ): Observable<ProcessEvent<StartData, null>> {
    return super.currentProcess(this.getKey(documentId));
  }

  protected _dispatchRequest(data: InputData): PollingProcess {
    const formData = new FormData();
    if (data.file) formData.append("file", data.file);
    if (data.prompt) formData.append("prompt", data.prompt);
    formData.append("document_type", data.documentType);
    return this.http.post<{
      requestId: string;
      documentId: string;
    }>("document-generation/generate", formData);
  }

  protected _retryRequest(prevTryData: {
    input: InputData;
    start?: StartData;
  }): PollingProcess {
    const file = prevTryData.input.file;
    const prompt = prevTryData.input.prompt;
    const documentType = prevTryData.input.documentType;
    const documentId = prevTryData.start?.documentId;
    const formData = new FormData();
    if (file) formData.append("file", file);
    if (prompt) formData.append("prompt", prompt);
    formData.append("document_type", documentType);
    formData.append("document_id", documentId);
    return this.http.post<{
      requestId: string;
      documentId: string;
    }>("document-generation/regenerate", formData);
  }

  protected _checkStatusRequest(
    requestId: string,
  ): Observable<{ status: string; data: null }> {
    return this.http
      .get<{ status: string }>(`document-generation/status/${requestId}`)
      .pipe(map((r) => ({ ...r, data: null })));
  }
}
